<template>
  <div id="step1" class="container page">

    <form v-if="state !== null">
      <div class="row mb-4">
        <form-item-group
            container-class="col"
            :required="true"
            :label="$t('adm.label.organization')"
            label-for="userOrg"
            :validation="v$.form.organization">
          <select1-organization v-model="form.organization" @update:model-value="loadContacts"/>
        </form-item-group>
        <form-item-group
            container-class="col"
            :required="true"
            :label="$t('adm.label.email')"
            label-for="userEmail"
            :validation="v$.form.email">
          <input
              type="email"
              class="form-control"
              id="userEmail"
              v-model="v$.form.email.$model"
              @input="loadContactsDelayed"
              autofocus
              aria-label="email">
        </form-item-group>
      </div>

      <div class="row mb-4">
        <form-item-group
            container-class="col"
            :required="true"
            :label="$t('adm.label.firstname')"
            label-for="userFirstname"
            :validation="v$.form.firstname">
          <input
              type="text"
              class="form-control"
              id="userFirstname"
              v-model="v$.form.firstname.$model"
              @input="loadContactsDelayed"
              aria-label="first name"/>
        </form-item-group>
        <form-item-group
            container-class="col"
            :required="true"
            :label="$t('adm.label.lastname')"
            label-for="userLastname"
            :validation="v$.form.lastname">
          <input
              type="text"
              class="form-control"
              id="userLastname"
              v-model="v$.form.lastname.$model"
              @input="loadContactsDelayed"
              aria-label="last name">
        </form-item-group>
      </div>
    </form>

    <the-overlay :active="state === 'loading'">
      <stihl-flag message="Select an existing user" class="mb-3"/>

      <alert-item class="alert-light" v-if="$arraySize(crmContacts) > 0 && !form.crmId && !form.email">
        {{ $t('adm.page.addUser.offerImport') }}
      </alert-item>
      <alert-item class="alert-danger" v-if="mustSelectContact">{{ $t('adm.page.addUser.mustImport') }}</alert-item>

      <data-table
          table-id="contactsTable"
          :columns="headers"
          :items="crmContacts"
          item-label="items"
          @item-click="onContactClick"
          :pageSizes="[5, 10, 25, 50]"
          :init-page-size="5">
        <template v-slot:email="{ item, index }">
          <input
              type="radio"
              class="form-check-input"
              name="contact"
              v-model="form.crmId"
              :value="item.crmId"
              @click="onContactClick(index)"
              aria-label="select contact">
          <span>{{ item.email }}</span>
        </template>
        <template v-slot:crmId="{ item }">
          <a
              :href="crmLink(item.crmId)"
              target="_blank"
              rel="noopener noreferrer"
              :title="$t('adm.page.sync.gotoCRM')"
              v-text="item.crmId"/>
        </template>
      </data-table>
    </the-overlay>

    <div class="mt-5">
      <input
          id="btnContinue"
          type="button"
          class="btn btn-primary w-100"
          :value="$t('adm.button.continue')"
          :disabled="v$.$invalid || mustSelectContact || state !== 'ready'"
          @click="toNextStep"
          @keyup.enter.space="toNextStep"/>
    </div>

  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Logger } from 'oidc-client-ts';
import TheOverlay from '@/components/ui/TheOverlay.vue';
import FormItemGroup from '@/components/ui/FormItemGroup.vue';
import useVuelidate from '@vuelidate/core';
import {
  emailNotInUse, maxLength, minLength, required, validEmail,
} from '@/components/validators';
import Select1Organization from '@/components/ui/Select1Organization.vue';
import { mapState } from 'pinia';
import DataTable, { TableColumns } from '@/components/ui/DataTable.vue';
import Backend, { CPF, CrmContact, PIN } from '@/components/backend';
import StihlFlag from '@/components/ui/StihlFlag.vue';
import AlertItem from '@/components/ui/AlertItem.vue';
import useAppStore from '@/components/store';
import { UserForm } from '@/views/Users/edit/index.vue';

export default defineComponent({
  name: 'AddStep1',
  components: {
    AlertItem,
    DataTable,
    FormItemGroup,
    Select1Organization,
    StihlFlag,
    TheOverlay,
  },
  emits: ['continue'],
  data: () => ({
    form: {
      email: '',
      firstname: '',
      lastname: '',
      organization: '',
      pin: { value: '' } as PIN,
    } as UserForm,
    crmContacts: [] as CrmContact[],
    state: null as null | 'loading' | 'loadDelayed' | 'ready' | 'sending',
    timer: 0,
    abortController: null as AbortController | null,
    // org: {} as OrganizationData,
  }),
  computed: {
    ...mapState(useAppStore, {
      myOrganization: 'myOrganization',
      myRole: 'getMyAdminRole',
    }),
    headers(): TableColumns {
      const cols = {
        email: { label: this.$t('adm.label.email'), sortable: true, selfRender: true },
        firstname: { label: this.$t('adm.label.firstname'), sortable: true },
        lastname: { label: this.$t('adm.label.lastname'), sortable: true },
      } as TableColumns;

      if (this.myRole !== 'userAdmin') {
        cols.crmId = { label: this.$t('adm.label.crmId'), sortable: true, selfRender: true };
      }

      return cols;
    },
    /**
     * if contacts exists for the email - must reuse it
     */
    mustSelectContact(): boolean {
      return this.crmContacts.length > 0 && !this.form.crmId && !!this.form.email && !this.v$.form.email.$invalid;
    },
  },
  methods: {
    setContacts(contacts: CrmContact[]) {
      this.crmContacts = contacts;
      // reset selection when the contacts are updates
      this.form.crmId = undefined;
    },

    loadContactsDelayed() {
      // Logger.log("reset");
      clearTimeout(this.timer);

      this.setContacts([]); // reset contacts

      // do not search if an invalid email was introduced
      if (this.form.email && this.v$.form.email.$invalid) return;

      this.state = 'loadDelayed'; // load delayed - show indicator

      // delay search to give user time to type
      Logger.debug('AddUser:1', 'set timer to load contacts', this.v$.form.email);
      this.timer = window.setTimeout(this.loadContacts, 1000);
    },
    async loadContacts() {
      Logger.debug('AddUser:1', 'LOAD');

      if (this.abortController) { // kill old request
        Logger.debug('AddUser:1', 'abort search');
        this.abortController.abort();
        this.abortController = null;
      }

      // check again in case it did not stop before
      if (this.form.email && this.v$.form.email.$invalid) return;

      const api = await Backend.validateToken();

      Logger.info('AddUser:1', 'loadContacts by:', JSON.stringify(this.form));
      this.state = 'loading';

      this.abortController = new AbortController();
      return api.withAbortController(this.abortController).searchCrmContacts({
        organization: this.form.organization,
        email: this.form.email,
        firstname: this.form.firstname,
        lastname: this.form.lastname,
        state: 'active',
      }, 'Id,FirstNameOfContact,LastNameOfContact,BusinessEmail,RolesOfContactCode,LanguageCode,TaxNumber')
          .then((r) => {
            Logger.debug('AddUser:1', 'contacts loaded');
            this.setContacts(r);
            this.state = 'ready';
          }).catch((e) => {
        Logger.error('AddUser:1', 'failed to load crm-contacts', e);
        this.state = 'ready';
        this.$toast.error(this.$t('adm.error.generic'));
      });
    },

    onContactClick(i: number) {
      // Logger.debug('[AddUser:1] onContactClick', i)

      const contact = this.crmContacts[i];

      this.form.crmId = contact.crmId;
      this.v$.form.email.$model = contact.email; // this will trigger validation of the email
      this.form.firstname = contact.firstname;
      this.form.lastname = contact.lastname;
      if (window.APP_CONFIG.roles.show) this.form.roles = contact.roles;
      this.form.preferredLanguage = contact.preferredLanguage;
      this.form.pin = contact.taxNumber ? new CPF(contact.taxNumber) : {} as PIN;
    },
    toNextStep() {
      this.$emit('continue', this.form);
    },
  },
  mounted() {
    // Logger.debug("AddUser:1","set-org", this.myOrganization)
    this.form.organization = (this.$route.query.org ?? this.myOrganization) as string;
    this.loadContacts();
  },
  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return {
      form: { // must match the fields in 'this.form'
        email: {
          required, maxLength: maxLength(254), validEmail, emailNotInUse,
        },
        firstname: { required, minLength: minLength(1), maxLength: maxLength(100) },
        lastname: { required, minLength: minLength(1), maxLength: maxLength(100) },
        organization: { required },
      },
    };
  },
});
</script>

<style>
#step1.page {
  display: flex;
  flex-flow: column;
  gap: 1.25rem;

  width: var(--stihl-container-max-width);
  padding: 2rem 0;
}

#contactsTable td:first-child {
  padding-left: .5rem;

  display: flex;
  align-items: center;
  gap: .7rem;
}

</style>
